import React, { useState } from 'react';
import { Split, Download, X, Layers, FileSpreadsheet, FileCheck } from 'lucide-react';
import { Button } from '../../../shared/components/Button';
import { StudioPage } from '../types';
import { extractStudioPagesToPdf, splitStudioPagesByRanges } from '../utils/pdfProcessor';
import { downloadPdfBytes } from '../../../core/export/pdfExporter';
import { sanitizeFilename } from '../../../core/export/documentValidator';
import { useToast } from '../../../shared/context/ToastContext';

interface ExtractPagesModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedPages: StudioPage[];
  allPages: StudioPage[];
}

export const ExtractPagesModal: React.FC<ExtractPagesModalProps> = ({
  isOpen,
  onClose,
  selectedPages,
  allPages,
}) => {
  const { showToast } = useToast();
  const [splitMode, setSplitMode] = useState<'selected' | 'range' | 'every_page'>('selected');
  const [fileName, setFileName] = useState('Extracted_Pages');
  const [rangeInput, setRangeInput] = useState(`1-${Math.min(3, allPages.length || 1)}`);
  const [extracting, setExtracting] = useState(false);

  if (!isOpen) return null;

  const handleExtractSelected = async () => {
    if (selectedPages.length === 0) {
      showToast('No pages selected. Please select at least one page.', 'warning');
      return;
    }
    setExtracting(true);
    try {
      const extractedBytes = await extractStudioPagesToPdf(selectedPages);
      downloadPdfBytes(extractedBytes, sanitizeFilename(fileName, 'pdf'));
      showToast(`Extracted ${selectedPages.length} pages to "${sanitizeFilename(fileName, 'pdf')}".`, 'success');
      onClose();
    } catch (err) {
      console.error('Failed to extract pages:', err);
      showToast('Error extracting selected pages.', 'error');
    } finally {
      setExtracting(false);
    }
  };

  const handleSplitByRanges = async () => {
    if (allPages.length === 0) return;
    setExtracting(true);
    try {
      // Parse ranges like "1-2, 3-5, 6"
      const parts = rangeInput.split(',').map((s) => s.trim()).filter(Boolean);
      const parsedRanges: Array<{ from: number; to: number; label: string }> = [];

      for (let i = 0; i < parts.length; i++) {
        const p = parts[i];
        if (p.includes('-')) {
          const [fStr, tStr] = p.split('-');
          const f = parseInt(fStr, 10);
          const t = parseInt(tStr, 10);
          if (!isNaN(f) && !isNaN(t) && f <= t) {
            parsedRanges.push({ from: f, to: t, label: `${fileName}_Part_${i + 1}_Pages_${f}-${t}` });
          }
        } else {
          const single = parseInt(p, 10);
          if (!isNaN(single)) {
            parsedRanges.push({ from: single, to: single, label: `${fileName}_Part_${i + 1}_Page_${single}` });
          }
        }
      }

      if (parsedRanges.length === 0) {
        showToast('Please enter valid page ranges (e.g. 1-2, 3-5).', 'warning');
        setExtracting(false);
        return;
      }

      const results = await splitStudioPagesByRanges(allPages, parsedRanges);
      for (const res of results) {
        downloadPdfBytes(res.bytes, sanitizeFilename(res.label, 'pdf'));
      }

      showToast(`Successfully split into ${results.length} PDF files.`, 'success');
      onClose();
    } catch (err) {
      console.error('Split ranges failed:', err);
      showToast('Error splitting pages by range.', 'error');
    } finally {
      setExtracting(false);
    }
  };

  const handleSplitEveryPage = async () => {
    if (allPages.length === 0) return;
    setExtracting(true);
    try {
      const ranges = allPages.map((_, i) => ({
        from: i + 1,
        to: i + 1,
        label: `${fileName}_Page_${i + 1}`,
      }));

      const results = await splitStudioPagesByRanges(allPages, ranges);
      for (const res of results) {
        downloadPdfBytes(res.bytes, sanitizeFilename(res.label, 'pdf'));
      }

      showToast(`Extracted all ${results.length} pages as individual PDFs.`, 'success');
      onClose();
    } catch (err) {
      console.error('Split every page failed:', err);
      showToast('Error splitting individual pages.', 'error');
    } finally {
      setExtracting(false);
    }
  };

  const handleExecute = () => {
    if (splitMode === 'selected') handleExtractSelected();
    else if (splitMode === 'range') handleSplitByRanges();
    else if (splitMode === 'every_page') handleSplitEveryPage();
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.5)',
        backdropFilter: 'blur(4px)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-4)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'var(--bg-surface)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-xl)',
          width: '100%',
          maxWidth: '520px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          border: '1px solid var(--border-subtle)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: 'var(--space-4) var(--space-6)',
            borderBottom: '1px solid var(--border-subtle)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <Split size={18} color="var(--primary-600)" />
            <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 600 }}>
              Split & Extract PDF Pages
            </h3>
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={onClose}
            aria-label="Close modal"
          >
            <X size={16} />
          </button>
        </div>

        {/* Mode Selector Tabs */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            padding: 'var(--space-3) var(--space-6)',
            backgroundColor: 'var(--bg-surface-subtle)',
            gap: 'var(--space-2)',
            borderBottom: '1px solid var(--border-subtle)',
          }}
        >
          <button
            type="button"
            className={`btn btn-sm ${splitMode === 'selected' ? 'btn-primary' : 'btn-secondary'}`}
            style={{ fontSize: '11px', padding: '6px 4px', justifyContent: 'center' }}
            onClick={() => setSplitMode('selected')}
          >
            <FileCheck size={13} />
            <span>Selected ({selectedPages.length})</span>
          </button>

          <button
            type="button"
            className={`btn btn-sm ${splitMode === 'range' ? 'btn-primary' : 'btn-secondary'}`}
            style={{ fontSize: '11px', padding: '6px 4px', justifyContent: 'center' }}
            onClick={() => setSplitMode('range')}
          >
            <Layers size={13} />
            <span>By Range</span>
          </button>

          <button
            type="button"
            className={`btn btn-sm ${splitMode === 'every_page' ? 'btn-primary' : 'btn-secondary'}`}
            style={{ fontSize: '11px', padding: '6px 4px', justifyContent: 'center' }}
            onClick={() => setSplitMode('every_page')}
          >
            <FileSpreadsheet size={13} />
            <span>All Single Pages</span>
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 'var(--space-6)', display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          {splitMode === 'selected' && (
            <div
              style={{
                padding: 'var(--space-3) var(--space-4)',
                backgroundColor: 'var(--primary-50)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--primary-800)',
              }}
            >
              Extracts <strong>{selectedPages.length}</strong> currently checked {selectedPages.length === 1 ? 'page' : 'pages'} into a single PDF document.
            </div>
          )}

          {splitMode === 'range' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              <div
                style={{
                  padding: 'var(--space-3) var(--space-4)',
                  backgroundColor: 'var(--bg-surface-subtle)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--text-secondary)',
                }}
              >
                Enter comma-separated page numbers or ranges (e.g. <code>1-2, 3-5, 6</code>). Total available pages: <strong>{allPages.length}</strong>.
              </div>
              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 600,
                    color: 'var(--text-secondary)',
                    marginBottom: 'var(--space-1)',
                  }}
                >
                  Page Ranges
                </label>
                <input
                  type="text"
                  className="input"
                  value={rangeInput}
                  onChange={(e) => setRangeInput(e.target.value)}
                  placeholder="e.g. 1-2, 3-4, 5"
                  style={{ width: '100%', fontFamily: 'var(--font-mono)' }}
                />
              </div>
            </div>
          )}

          {splitMode === 'every_page' && (
            <div
              style={{
                padding: 'var(--space-3) var(--space-4)',
                backgroundColor: 'var(--primary-50)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--primary-800)',
              }}
            >
              Splits every page of the document into its own standalone 1-page PDF file (Total: <strong>{allPages.length}</strong> files).
            </div>
          )}

          <div>
            <label
              style={{
                display: 'block',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 600,
                color: 'var(--text-secondary)',
                marginBottom: 'var(--space-1)',
              }}
            >
              Base File Name
            </label>
            <input
              type="text"
              className="input"
              value={fileName}
              onChange={(e) => setFileName(e.target.value)}
              placeholder="e.g. Problem_Set_Split"
              style={{ width: '100%' }}
            />
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: 'var(--space-4) var(--space-6)',
            borderTop: '1px solid var(--border-subtle)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 'var(--space-3)',
          }}
        >
          <Button variant="ghost" onClick={onClose} disabled={extracting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            icon={<Download size={14} />}
            onClick={handleExecute}
            loading={extracting}
            disabled={splitMode === 'selected' && selectedPages.length === 0}
          >
            {splitMode === 'selected'
              ? `Extract Selected (${selectedPages.length})`
              : splitMode === 'range'
              ? 'Split by Ranges'
              : `Split All (${allPages.length} Pages)`}
          </Button>
        </div>
      </div>
    </div>
  );
};
